import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Table,
  Badge,
  Tab,
  Nav,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  CreditCard,
  Calendar,
  CheckCircle,
  Info,
  Person,
  FileText,
  Key,
  EnvelopeFill,
  TelephoneFill,
  BuildingFill,
  GeoAltFill,
  Globe,
} from "react-bootstrap-icons";
import FromPageIndicator from "../Common/FromPageIndicator";
// import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { img } from "framer-motion/client";
import CustomAlert from "../CustomAlert/CustomAlert";

const RegistrationPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",
    name: "",
    mobile: "",
    email: "",
    password: "",
    confirmPassword: "",
    category: "",
    membershipId: "",
    institute: "",
    city: "",
    country: "",
  });

  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState("fees");
  // const [earlyBirdPassed, setEarlyBirdPassed] = useState(null);
  // const [regularPassed, setRegularPassed] = useState(null);
  const [activePeriod, setActivePeriod] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [generatedUserId, setGeneratedUserId] = useState("");

  // Function to generate the next user ID
  const generateNextUserId = (existingUsers) => {
    if (!existingUsers || existingUsers.length === 0) {
      return "TNPY2025001";
    }

    // Filter users with TNPY2025 format and extract numbers
    const tnpyUsers = existingUsers
      .filter((user) => user.userId && user.userId.startsWith("TNPY2025"))
      .map((user) => {
        const numberPart = user.userId.replace("TNPY2025", "");
        return parseInt(numberPart, 10);
      })
      .filter((num) => !isNaN(num));

    // Find the highest number
    const highestNumber = tnpyUsers.length > 0 ? Math.max(...tnpyUsers) : 0;

    // Generate next number with leading zeros
    const nextNumber = highestNumber + 1;
    const paddedNumber = nextNumber.toString().padStart(3, "0");

    return `TNPY2025${paddedNumber}`;
  };

  // Function to determine if a date has passed
  const isDatePassed = (dateString) => {
    const currentDate = new Date(); // Using fixed date for testing
    const targetDate = new Date(dateString);
    return currentDate > targetDate;
  };

  const handleFileChange = (e) => {
    setFormData({
      ...formData,
      // ageProofDocument: e.target.files[0],
      [formData.category === "student"
        ? "bonafideCertificate"
        : "ageProofDocument"]: e.target.files[0],
    });
  };

  useEffect(() => {
    const currentDate = new Date();

    // Define deadlines
    const earlyBirdDeadline = new Date("2025-08-15T23:59:59");
    const regularDeadline = new Date("2025-09-10T23:59:59");
    const conferenceStartDate = new Date("2025-09-26T00:00:00");

    // Check which periods have passed
    const earlyBirdPassed = currentDate > earlyBirdDeadline;
    const regularPassed = currentDate > regularDeadline;
    const conferenceStarted = currentDate >= conferenceStartDate;

    console.log("Current Date:", currentDate);
    console.log("Early Bird Passed:", earlyBirdPassed);
    console.log("Regular Passed:", regularPassed);
    console.log("Conference Started:", conferenceStarted);

    // Determine active period
    if (!earlyBirdPassed) {
      setActivePeriod("early-bird");
    } else if (!regularPassed) {
      setActivePeriod("regular");
    } else if (!conferenceStarted) {
      setActivePeriod("onsite");
    } else {
      setActivePeriod("conference-started"); // Conference has started
    }
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleLoginChange = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value,
    });
  };

  // Function to push registration data to database
  const pushRegistrationToDB = async (userData) => {
    setIsLoading(true);

    try {
      // First, get existing users to generate the next user ID
      const { data: existingUsers } = await axios.get(
        "https://api.dremerz.net/api/DZ_8921_Ampicon_Create_Users_UserList"
      );

      // Check for duplicate email
      const duplicate = existingUsers.find(
        (user) => user.emailId?.toLowerCase() === userData.email?.toLowerCase()
      );

      if (duplicate) {
        setErrorMessage("User with this email ID already exists");
        setShowError(true);
        return { success: false, error: "Email Already Exists" };
      }

      // Generate the next user ID
      const nextUserId = generateNextUserId(existingUsers);

      const pushingData = new FormData();

      // Add the generated user ID
      pushingData.append("ageProof", userData.ageProofDocument);
      pushingData.append("userId", nextUserId);
      pushingData.append("userTitle", userData.title);
      pushingData.append("userName", userData.name);
      pushingData.append("mobileNumber", userData.mobile);
      pushingData.append("emailId", userData.email);
      pushingData.append("password", userData.password);
      pushingData.append("registrationCategory", userData.category);
      pushingData.append("membershipId", userData.membershipId);
      pushingData.append("instituteName", userData.institute);
      pushingData.append("city", userData.city);
      pushingData.append("country", userData.country);

      pushingData.append("paymentStatus", "pending");
      if (userData.category === "student" && userData.bonafideCertificate) {
        pushingData.append("bonafideCertificate", userData.bonafideCertificate);
      }

      const response = await axios.post(
        "https://api.dremerz.net/api/DZ_8921_Ampicon_Create_Users_UserList",
        pushingData
      );

      const data = response.data;
      return { success: true, data, userId: nextUserId };
    } catch (error) {
      console.error("Registration error:", error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Function to authenticate user
  const authenticateUser = async (credentials) => {
    setIsLoading(true);

    try {
      const response = await fetch(
        "https://api.dremerz.net/api/DZ_8921_Ampicon_Create_Users_UserList",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Authentication failed");
      }
      const user =
        data &&
        data.find(
          (u) =>
            u.emailId === credentials.email &&
            u.password === credentials.password
        );

      if (user) {
        console.log(user);

        // Store both the database ID and the custom user ID
        sessionStorage.setItem("userId", user.id); // Use custom userId if available, fallback to database id
        localStorage.setItem("userId", user.id);
        sessionStorage.setItem("userDbId", user.userId); // Store database ID separately if needed
        localStorage.setItem("userDbId", user.userId);
        sessionStorage.setItem("userName", user.userName);
        localStorage.setItem("userName", user.userName);
        sessionStorage.setItem("emailId", user.emailId);
        localStorage.setItem("emailId", user.emailId);
        return { success: true, data };
      } else {
        return { success: false, data };
      }
    } catch (error) {
      console.error("Login error:", error);
      return { success: false, error: "Please register to login" };
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (
      !formData.name ||
      !formData.email ||
      !formData.mobile ||
      !formData.password
    ) {
      setShowError(true);
      setErrorMessage("Please fill in all required fields");
      return;
    }

    // Password confirmation validation
    if (formData.password !== formData.confirmPassword) {
      setShowError(true);
      setErrorMessage("Passwords do not match");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setShowError(true);
      setErrorMessage("Please enter a valid email address");
      return;
    }

    // Mobile validation (basic)
    const mobileRegex = /^\d{10}$/;
    if (!mobileRegex.test(formData.mobile)) {
      setShowError(true);
      setErrorMessage("Please enter a valid 10-digit mobile number");
      return;
    }

    // If we're here, validation passed
    const result = await pushRegistrationToDB(formData);

    if (result.success) {
      setRegistrationSuccess(true);
      // Show success message with the generated user ID
      setErrorMessage(
        `Registration successful! Your User ID is: ${result.userId}`
      );
      setShowError(false);

      // Clear form or redirect user
      setTimeout(() => {
        setActiveTab("login");
        setRegistrationSuccess(false);
      }, 4000); // Increased timeout to show the user ID
    } else {
      setShowError(true);
      setErrorMessage(result.error || "Registration failed. Please try again.");
      setTimeout(() => {
        setShowError(false);
        setErrorMessage("");
      }, 3000);
    }
  };

  const FullScreenSuccessAlert = ({ show, onClose, registrationNumber }) => {
    if (!show) return null;

    return (
      <div className="success-overlay">
        <div className="success-modal">
          <button className="close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>

          <div className="success-icon-large">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
          </div>

          <h2 className="success-title">🎉 Registration Successful!</h2>

          <p className="success-message">
            Congratulations! Your account has been created successfully. Welcome
            to TNPY AMPICON 2025!
          </p>

          {/* <div className="registration-display">
          <div className="registration-label">Registration Number</div>
          <div className="registration-value">{registrationNumber}</div>
        </div> */}

          <div className="success-actions">
            <button className="btn btn-light-outline" onClick={onClose}>
              Close
            </button>
            <button
              className="btn btn-light-solid"
              onClick={() => {
                onClose();
                // Switch to login tab - you'll need to pass this function as prop
                setActiveTab("login");
              }}
            >
              Login Now
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!loginData.email || !loginData.password) {
      setShowError(true);
      setErrorMessage("Please enter both email and password");
      return;
    }

    const result = await authenticateUser(loginData);

    if (result.success) {
      setLoginSuccess(true);
      // Redirect to dashboard or user profile
      setTimeout(() => {
        navigate("/paymentPage");
        window.location.reload();
      }, 1500);
    } else {
      setShowError(true);
      setErrorMessage(
        result.error || "Login failed. Please check your credentials."
      );
      setTimeout(() => {
        setShowError(false);
        setErrorMessage("");
      }, 2000);
    }
  };

  const getColumnStyle = (period) => {
    const isActive = activePeriod === period;
    const isPassed = (() => {
      const currentDate = new Date();
      switch (period) {
        case "early-bird":
          return currentDate > new Date("2025-08-15T23:59:59");
        case "regular":
          return currentDate > new Date("2025-09-10T23:59:59");
        case "onsite":
          return currentDate >= new Date("2025-09-26T00:00:00");
        case "workshop":
          return currentDate > new Date("2025-09-26T23:59:59");
        default:
          return false;
      }
    })();

    return {
      minWidth: period === "workshop" ? "200px" : "180px",
      opacity: isPassed ? 0.4 : isActive ? 1 : 0.7,
      backgroundColor:
        isActive && !isPassed
          ? (() => {
              switch (period) {
                case "early-bird":
                  return "rgba(240, 255, 240, 0.8)";
                case "regular":
                  return "rgba(255, 248, 220, 0.8)";
                case "onsite":
                  return "rgba(255, 240, 240, 0.8)";
                case "workshop":
                  return "rgba(240, 248, 255, 0.8)";
                default:
                  return "transparent";
              }
            })()
          : "transparent",
      position: "relative",
      textDecoration: isPassed ? "line-through" : "none",
      color: isPassed ? "#6c757d" : "inherit",
    };
  };

  const calculateDaysLeft = (targetDate = "2025-09-10") => {
    const deadline = new Date(targetDate).getTime();
    const today = new Date().getTime();
    const daysLeft = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
    return daysLeft > 0 ? daysLeft : 0;
  };

  return (
    <div style={{ backgroundColor: "#FFF4FA" }}>
      <style>
        {`
    .custom-nav-link.active {
      background-color: #2582A1 !important;
      border-color: #2582A1 !important;
    }
    .custom-nav-link:not(.active) {
      color: #2582A1 !important;
    }
    .form-control:focus {
      border-color: rgba(37, 130, 161, 0.4);
      box-shadow: 0 0 0 0.25rem rgba(37, 130, 161, 0.25);
    }
    .btn-primary {
      background-color: #2582A1;
      border-color: #2582A1;
    }
    .btn-primary:hover,
    .btn-primary:focus {
      background-color: #1f6b86;
      border-color: #1f6b86;
    }
    .btn-outline-primary {
      color: #2582A1;
      border-color: #2582A1;
    }
    .btn-outline-primary:hover {
      background-color: #2582A1;
      border-color: #2582A1;
    }
    .link-primary {
      color: #2582A1 !important;
      cursor: pointer;
    }
    .link-primary:hover {
      text-decoration: underline !important;
    }
 

/* Full Screen Success Overlay */
.success-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease-out;
}

.success-modal {
  background: linear-gradient(135deg, #2582A1 0%, #1f6b86 100%);
  border-radius: 20px;
  padding: 40px;
  max-width: 500px;
  width: 90%;
  text-align: center;
  color: white;
  box-shadow: 0 20px 60px rgba(37, 130, 161, 0.4);
  animation: slideInScale 0.5s ease-out;
  position: relative;
}

.success-modal .close-btn {
  position: absolute;
  top: 15px;
  right: 20px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.success-modal .close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.success-icon-large {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  animation: bounceIn 0.8s ease-out 0.3s both;
}

.success-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 15px;
  animation: slideInUp 0.6s ease-out 0.4s both;
}

.success-message {
  font-size: 1.1rem;
  margin-bottom: 20px;
  opacity: 0.95;
  animation: slideInUp 0.6s ease-out 0.5s both;
}

.user-id-display {
  background: rgba(255, 255, 255, 0.15);
  padding: 15px 20px;
  border-radius: 12px;
  margin: 20px 0;
  border: 2px solid rgba(255, 255, 255, 0.3);
  animation: slideInUp 0.6s ease-out 0.6s both;
}

.user-id-display .user-id-label {
  font-size: 0.9rem;
  opacity: 0.8;
  margin-bottom: 5px;
}

.user-id-display .user-id-value {
  font-family: 'Courier New', monospace;
  font-size: 1.3rem;
  font-weight: bold;
  letter-spacing: 2px;
}

.success-actions {
  margin-top: 25px;
  animation: slideInUp 0.6s ease-out 0.7s both;
}

.success-actions .btn {
  margin: 0 10px;
  padding: 12px 25px;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-light-outline {
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.5);
  color: white;
}

.btn-light-outline:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: white;
  color: white;
  transform: translateY(-2px);
}

.btn-light-solid {
  background: white;
  border: 2px solid white;
  color: #2582A1;
}

.btn-light-solid:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-2px);
  color: #1f6b86;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideInScale {
  from {
    opacity: 0;
    transform: scale(0.8) translateY(50px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounceIn {
  0%, 20%, 40%, 60%, 80% {
    animation-timing-function: cubic-bezier(0.215, 0.610, 0.355, 1.000);
  }
  0% {
    opacity: 0;
    transform: scale3d(.3, .3, .3);
  }
  20% {
    transform: scale3d(1.1, 1.1, 1.1);
  }
  40% {
    transform: scale3d(.9, .9, .9);
  }
  60% {
    opacity: 1;
    transform: scale3d(1.03, 1.03, 1.03);
  }
  80% {
    transform: scale3d(.97, .97, .97);
  }
  to {
    opacity: 1;
    transform: scale3d(1, 1, 1);
  }
}
  `}
      </style>

      {/* Custom Alerts */}
      <CustomAlert
        show={showError}
        onClose={() => setShowError(false)}
        variant="danger"
        title="Registration Error"
        message={errorMessage}
        position="center"
        size="normal"
        dismissible={true}
        autoClose={true}
        autoCloseDelay={6000}
        animation="slide"
        overlay={true}
      />

      <CustomAlert
        show={registrationSuccess}
        onClose={() => {
          setRegistrationSuccess(false);
          setActiveTab("login");
        }}
        variant="success"
        title="🎉 Registration Successful!"
        message={`Congratulations! Your account has been created successfully. Your User ID is: ${generatedUserId}. Welcome to TNPY AMPICON 2025!`}
        position="center"
        size="large"
        dismissible={true}
        animation="bounce"
        overlay={true}
        actionButton={
          <div className="d-flex gap-2 mt-3">
            <Button
              variant="outline-success"
              size="sm"
              onClick={() => {
                setRegistrationSuccess(false);
              }}
            >
              Close
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={() => {
                setRegistrationSuccess(false);
                setActiveTab("login");
              }}
            >
              Login Now
            </Button>
          </div>
        }
      />

      <CustomAlert
        show={loginSuccess}
        onClose={() => setLoginSuccess(false)}
        variant="success"
        title="Login Successful!"
        message="Welcome back! Redirecting you to the payment dashboard..."
        position="center"
        size="normal"
        dismissible={false}
        autoClose={true}
        autoCloseDelay={2000}
        animation="fade"
        overlay={true}
        icon="✅"
      />

      <FromPageIndicator />

      <div className="text-dark py-4 pb-4">
        <Container>
          <h1 className="text-center mb-2" style={{ color: "#2582A1" }}>
            TNPY AMPICON 2025 Registration
          </h1>
          <p className="text-center mb-0">
            28th Annual Conference of Association of Medical Physicists of India
            (Tamil Nadu & Puducherry Chapter)
          </p>
        </Container>
      </div>

      <Container className="pb-5">
        <Tab.Container
          id="registration-tabs"
          defaultActiveKey="registration"
          activeKey={activeTab}
          onSelect={(k) => {
            setActiveTab(k);
            setShowError(false);
          }}
        >
          <Nav variant="pills" className="nav-fill mb-4">
            <Nav.Item>
              <Nav.Link
                eventKey="fees"
                className="d-flex align-items-center justify-content-center custom-nav-link"
              >
                <CreditCard className="me-2" /> Registration Fees
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="registration"
                className="d-flex align-items-center justify-content-center custom-nav-link"
              >
                <Person className="me-2" /> Registration Form
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="login"
                className="d-flex align-items-center justify-content-center custom-nav-link"
              >
                <FileText className="me-2" /> Login Form
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <Tab.Content>
            <Tab.Pane eventKey="registration">
              <Row>
                {/* <Col lg={8} className="mb-4">
                  <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
                      <h3 className="mb-0">Create Your Account</h3>
                    </Card.Header>
                    <Card.Body className="px-4 pb-4">
                      <Form onSubmit={handleRegistrationSubmit}>
                        <Row className="mb-3">
                          <Col md={6} className="mb-3">
                            <Form.Group controlId="formTitle">
                              <Form.Label className="fw-semibold">
                                Title <span className="text-danger">*</span>
                              </Form.Label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <Person />
                                </span>
                                <Form.Select
                                  name="title"
                                  value={formData.title}
                                  onChange={handleChange}
                                  required
                                  className="py-2"
                                >
                                  <option value="">Select a title</option>
                                  <option value="Dr.">Dr.</option>
                                  <option value="Mr.">Mr.</option>
                                  <option value="Ms.">Ms.</option>
                                  <option value="Mrs.">Mrs.</option>
                                  <option value="Prof.">Prof.</option>
                                </Form.Select>
                              </div>
                            </Form.Group>
                          </Col>

                          <Col md={6} className="mb-3">
                            <Form.Group controlId="formName">
                              <Form.Label className="fw-semibold">
                                Full Name <span className="text-danger">*</span>
                              </Form.Label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <Person />
                                </span>
                                <Form.Control
                                  type="text"
                                  placeholder="Enter your full name"
                                  name="name"
                                  value={formData.name}
                                  onChange={handleChange}
                                  required
                                  className="py-2"
                                />
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={6} className="mb-3 mb-md-0">
                            <Form.Group controlId="formEmail">
                              <Form.Label className="fw-semibold">
                                Email Address{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <EnvelopeFill />
                                </span>
                                <Form.Control
                                  type="email"
                                  placeholder="Enter your email address"
                                  name="email"
                                  value={formData.email}
                                  onChange={handleChange}
                                  required
                                  className="py-2"
                                />
                              </div>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group controlId="formMobile">
                              <Form.Label className="fw-semibold">
                                Mobile Number{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <TelephoneFill />
                                </span>
                                <Form.Control
                                  type="text"
                                  placeholder="Enter your 10-digit mobile number"
                                  name="mobile"
                                  value={formData.mobile}
                                  onChange={handleChange}
                                  required
                                  className="py-2"
                                />
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={6} className="mb-3 mb-md-0">
                            <Form.Group controlId="formPassword">
                              <Form.Label className="fw-semibold">
                                Password <span className="text-danger">*</span>
                              </Form.Label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <Key />
                                </span>
                                <Form.Control
                                  type={showPassword ? "text" : "password"}
                                  placeholder="Create a strong password"
                                  name="password"
                                  value={formData.password}
                                  onChange={handleChange}
                                  required
                                  className="py-2"
                                />
                                <Button
                                  variant="outline-secondary"
                                  type="button"
                                  onClick={() => setShowPassword(!showPassword)}
                                  className="border-start-0"
                                >
                                  {showPassword ? <EyeSlash /> : <Eye />}
                                </Button>
                              </div>
                              <Form.Text className="text-muted">
                                Password must be at least 8 characters
                              </Form.Text>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group controlId="formConfirmPassword">
                              <Form.Label className="fw-semibold">
                                Confirm Password{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <Key />
                                </span>
                                <Form.Control
                                  type={
                                    showConfirmPassword ? "text" : "password"
                                  }
                                  placeholder="Confirm your password"
                                  name="confirmPassword"
                                  value={formData.confirmPassword}
                                  onChange={handleChange}
                                  required
                                  className="py-2"
                                />
                                <Button
                                  variant="outline-secondary"
                                  type="button"
                                  onClick={() =>
                                    setShowConfirmPassword(!showConfirmPassword)
                                  }
                                  className="border-start-0"
                                >
                                  {showConfirmPassword ? <EyeSlash /> : <Eye />}
                                </Button>
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={12}>
                            <Form.Group controlId="formGeneralInstitute">
                              <Form.Label className="fw-semibold">
                                Institution/Organization
                              </Form.Label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <BuildingFill />
                                </span>
                                <Form.Control
                                  type="text"
                                  placeholder="Enter your institution name"
                                  name="institute"
                                  value={formData.institute}
                                  onChange={handleChange}
                                  className="py-2"
                                />
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={6} className="mb-3 mb-md-0">
                            <Form.Group controlId="formCity">
                              <Form.Label className="fw-semibold">
                                City
                              </Form.Label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <GeoAltFill />
                                </span>
                                <Form.Control
                                  type="text"
                                  placeholder="Enter city"
                                  name="city"
                                  value={formData.city}
                                  onChange={handleChange}
                                  className="py-2"
                                />
                              </div>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="formCountry">
                              <Form.Label className="fw-semibold">
                                Country
                              </Form.Label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  {formData.country === "India" ? (
                                    <img
                                      src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/bc/Flag_of_India.png/2560px-Flag_of_India.png"
                                      style={{ width: "20px", heigh: "20px" }}
                                    />
                                  ) : formData.country === "Afghanistan" ? (
                                    <img
                                      src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSStInjoij0GtXE9qsDWIVjefpd6s9NNdi7mw&s"
                                      style={{ width: "20px", heigh: "20px" }}
                                    />
                                  ) : formData.country === "Bangladesh" ? (
                                    <img
                                      src="https://cdn.pixabay.com/photo/2020/02/21/05/49/bangladesh-4866534_960_720.png"
                                      style={{ width: "20px", heigh: "20px" }}
                                    />
                                  ) : formData.country === "Bhutan" ? (
                                    <img
                                      src="https://upload.wikimedia.org/wikipedia/commons/thumb/9/91/Flag_of_Bhutan.svg/2560px-Flag_of_Bhutan.svg.png"
                                      style={{ width: "20px", heigh: "20px" }}
                                    />
                                  ) : formData.country === "Maldives" ? (
                                    <img
                                      src="https://upload.wikimedia.org/wikipedia/commons/thumb/0/0f/Flag_of_Maldives.svg/2560px-Flag_of_Maldives.svg.png"
                                      style={{ width: "20px", heigh: "20px" }}
                                    />
                                  ) : formData.country === "Nepal" ? (
                                    <img
                                      src="https://upload.wikimedia.org/wikipedia/commons/thumb/9/9b/Flag_of_Nepal.svg/630px-Flag_of_Nepal.svg.png"
                                      style={{ width: "20px", heigh: "20px" }}
                                    />
                                  ) : formData.country === "Pakistan" ? (
                                    <img
                                      src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/32/Flag_of_Pakistan.svg/2560px-Flag_of_Pakistan.svg.png"
                                      style={{ width: "20px", heigh: "20px" }}
                                    />
                                  ) : formData.country === "Sri Lanka" ? (
                                    <img
                                      src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/11/Flag_of_Sri_Lanka.svg/1024px-Flag_of_Sri_Lanka.svg.png"
                                      style={{ width: "20px", heigh: "20px" }}
                                    />
                                  ) : formData.country === "Other" ? (
                                    <Globe />
                                  ) : (
                                    <Globe />
                                  )}
                                </span>
                                <Form.Select
                                  as="select"
                                  name="country"
                                  value={formData.country}
                                  onChange={handleChange}
                                  className="py-2"
                                >
                                  <option value="">Select Your Country</option>
                                  <option value="India">India</option>
                                  <option value="Afghanistan">
                                    Afghanistan
                                  </option>
                                  <option value="Bangladesh">Bangladesh</option>
                                  <option value="Bhutan">Bhutan</option>
                                  <option value="Maldives">Maldives</option>
                                  <option value="Nepal">Nepal</option>
                                  <option value="Pakistan">Pakistan</option>
                                  <option value="Sri Lanka">Sri Lanka</option>
                                  <option value="Other">Other</option>
                                </Form.Select>
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Form.Group controlId="formCategory" className="mb-3">
                          <Form.Label className="fw-semibold">
                            Registration Category{" "}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            as="select"
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            required
                            className="py-2"
                          >
                            <option value="">Select Category</option>
                            <option value="ampi">AMPI & AROI Member</option>
                            <option value="non-ampi">
                              Non AMPI & non AROI Member
                            </option>
                            <option value="associate">
                              Associate Delegate
                            </option>
                            <option value="student">
                              Student (Medical Physics)
                            </option>
                            <option value="oncologist&physicist">
                              Oncologist + Physicist Team
                            </option>
                            <option value="oncology-or-physicist">
                              Oncologist or Medical Physicist
                            </option>
                            <option value="tradedelegates">
                              Trade Delegates
                            </option>
                            <option value="overseas">Overseas Delegate</option>
                            <option value="ampi-senior">
                              AMPI Senior Members
                            </option>
                            <option value="apollo-staff">Apollo Staff</option>
                            <option value="rtt">RTT</option>
                          </Form.Control>
                        </Form.Group>

                        {formData.category === "ampi" ||
                          (formData.category === "ampi-senior" && (
                            <Form.Group
                              controlId="formMembership"
                              className="mb-3"
                            >
                              <Form.Label className="fw-semibold">
                                AMPI or AROI Membership ID{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Enter your membership ID"
                                name="membershipId"
                                value={formData.membershipId}
                                onChange={handleChange}
                                required
                                className="py-2"
                              />
                            </Form.Group>
                          ))}
                        {formData.category === "ampi-senior" && (
                          <Form.Group
                            controlId="formDocumentUpload"
                            className="mb-3"
                          >
                            <Form.Label className="fw-semibold">
                              Submit Any Age Proof{" "}
                              <span className="text-danger">
                                Accepted File Formats (pdf, doc, docx, jpg,
                                jpeg, png)
                              </span>
                            </Form.Label>
                            <Form.Control
                              type="file"
                              name="ageProofDocument"
                              onChange={handleFileChange}
                              required
                              className="py-2"
                              accept=".pdf, .doc, .docx, .jpg, .jpeg, .png"
                            />
                          </Form.Group>
                        )}

                        {formData.category === "apollo-staff" && (
                          <>
                            <Form.Group
                              controlId="formEmployeeId"
                              className="mb-3"
                            >
                              <Form.Label className="fw-semibold">
                                Employee ID{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Enter your Employee ID"
                                name="employeeId"
                                value={formData.employeeId || ""}
                                onChange={handleChange}
                                required
                                className="py-2"
                              />
                            </Form.Group>
                            <Form.Group
                              controlId="formApolloInstitute"
                              className="mb-3"
                            >
                              <Form.Label className="fw-semibold">
                                Institution Name{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Enter your Institution Name"
                                name="apolloInstitute"
                                value={formData.apolloInstitute || ""}
                                onChange={handleChange}
                                required
                                className="py-2"
                              />
                            </Form.Group>
                          </>
                        )}

                        {formData.category === "student" && (
                          <Form.Group
                            controlId="formBonafideUpload"
                            className="mb-3"
                          >
                            <Form.Label className="fw-semibold">
                              Upload Bonafide Certificate{" "}
                              <span className="text-danger">
                                Accepted File Formats (pdf, doc, docx, jpg,
                                jpeg, png)
                              </span>
                            </Form.Label>
                            <Form.Control
                              type="file"
                              name="bonafideCertificate"
                              onChange={handleFileChange}
                              required
                              className="py-2"
                              accept=".pdf, .doc, .docx, .jpg, .jpeg, .png"
                            />
                          </Form.Group>
                        )}

                        <div className="d-grid">
                          <Button
                            type="submit"
                            size="lg"
                            className="py-2 btn-primary"
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  className="me-2"
                                />
                                Processing Registration...
                              </>
                            ) : (
                              "Create Account"
                            )}
                          </Button>
                        </div>
                        <div className="text-center mt-4 pt-3 border-top">
                          <p className="mb-0">
                            Already have an account?{" "}
                            <a
                              href="/login-page"
                              className="link-primary text-decoration-none"
                            >
                              Login Here
                            </a>
                          </p>
                        </div>
                      </Form>
                    </Card.Body>
                  </Card>
                </Col> */}

                <Col lg={8} className="mb-4">
                  <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
                      <h3 className="mb-0">Online Registration Closed</h3>
                      <p
                        className="mb-0"
                        style={{ color: "#2782A0", fontWeight: "bold" }}
                      >
                        Early Bird & Regular Registration Expired.{" "}
                        <span
                          style={{
                            animation: "blinker 3s linear infinite",
                            color: "red",
                          }}
                        >
                          Only On-Site Registration is Open.
                        </span>
                      </p>

                      <style>
                        {`
    @keyframes blinker {
      50% { opacity: 0; }
    }
  `}
                      </style>
                    </Card.Header>
                    <Card.Body className="px-4 pb-4">
                      <div className="table-responsive">
                        <table className="table table-bordered text-center align-middle">
                          <thead
                            style={{
                              backgroundColor: "#2782A0",
                              color: "#fff",
                            }}
                          >
                            <tr>
                              <th>Category</th>
                              <th>On-Site Registration</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>Oncologist + Physicist Team</td>
                              <td>₹ 5000 / $200</td>
                            </tr>
                            <tr>
                              <td>Oncologist or Medical Physicist</td>
                              <td>₹ 3000 / $120</td>
                            </tr>
                            <tr>
                              <td>AMPI & AROI Members</td>
                              <td>₹ 2,000</td>
                            </tr>
                            <tr>
                              <td>Non-AMPI & AROI Members</td>
                              <td>₹ 3,000</td>
                            </tr>
                            <tr>
                              <td>Associate Delegates</td>
                              <td>₹ 1,500</td>
                            </tr>
                            <tr>
                              <td>Students (Medical Physics, PG, PhD)</td>
                              <td>₹ 1,500</td>
                            </tr>
                            <tr>
                              <td>RTT</td>
                              <td>₹ 1000</td>
                            </tr>
                            <tr>
                              <td>Trade Delegates</td>
                              <td>₹ 7,000</td>
                            </tr>
                            <tr>
                              <td>Overseas Delegates</td>
                              <td>$ 150</td>
                            </tr>
                            <tr>
                              <td>AMPI Senior Members (60+)</td>
                              <td>Complimentary (* ₹1 Service Charge)</td>
                            </tr>
                            <tr>
                              <td>Apollo Staff</td>
                              <td>Complimentary (* ₹1 Service Charge)</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col lg={4}>
                  <div className="sticky-top" style={{ top: "2rem" }}>
                    <Card className="border-0 shadow-sm mb-4">
                      <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
                        <h3 className="mb-0">Days to remember</h3>
                      </Card.Header>
                      <Card.Body className="px-4 pb-4">
                        <Alert
                          variant="success"
                          className="d-flex align-items-center mb-4"
                        >
                          <Calendar className="me-2" size={20} />
                          <div>
                            <strong>On-Site Registration</strong>
                            <p className="mb-0">
                              {/* Ends in {calculateDaysLeft()} days */}
                              Opened
                            </p>
                          </div>
                        </Alert>

                        <ul className="list-unstyled mb-4">
                          <li className="d-flex align-items-center mb-2">
                            <CheckCircle className="text-success me-2" />
                            <span>
                              Conference Dates: 26th and 27th September 2025
                            </span>
                          </li>
                          <li className="d-flex align-items-center mb-2">
                            <CheckCircle className="text-success me-2" />
                            <span>
                              Pre-conference Hands-on Workshop: 26th September
                              2025
                            </span>
                          </li>
                          <li className="d-flex align-items-center mb-2">
                            <CheckCircle className="text-success me-2" />
                            <span>
                              Venue: Auditorium, Apollo Cancer Centre -
                              Vanagaram, Chennai
                            </span>
                          </li>
                          <li className="d-flex align-items-center mb-2">
                            <CheckCircle className="text-success me-2" />
                            <span>
                              Theme: "Solving New Challenges in Personalised
                              Oncology"
                            </span>
                          </li>
                        </ul>

                        <div className="d-grid">
                          <Button
                            variant="outline-primary"
                            onClick={() => setActiveTab("fees")}
                            className="d-flex align-items-center justify-content-center"
                          >
                            <Info className="me-2" /> View Registration Fees
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>

                    <Card className="border-0 shadow-sm bg-light">
                      <Card.Body className="p-4">
                        <h5>Need Help?</h5>
                        <p className="mb-0">
                          For registration queries, contact us at:
                        </p>
                        <p className="mb-0">
                          <strong>Email:</strong>{" "}
                          <a href="mailto:tnpyampicon2025@gmail.com">
                            tnpyampicon2025@gmail.com
                          </a>
                        </p>

                        <p>
                          <strong>Phone:</strong> +91 9994432135
                        </p>
                      </Card.Body>
                    </Card>
                  </div>
                </Col>
              </Row>
            </Tab.Pane>

            <Tab.Pane eventKey="fees">
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
                  <div className="d-flex justify-content-between align-items-center flex-wrap">
                    <h3 className="mb-0">Conference Registration Charges</h3>
                    <div>
                      <Button
                        className="mt-2 mt-sm-0 me-2 btn-primary"
                        onClick={() => setActiveTab("registration")}
                      >
                        Register Now
                      </Button>
                      <Button
                        className="mt-2 mt-sm-0 btn-primary"
                        onClick={() => setActiveTab("login")}
                      >
                        Login Now
                      </Button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body className="px-4 pb-4">
                  <div className="table-responsive">
                    <Table hover className="border">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ minWidth: "180px" }}>Category</th>
                          <th style={getColumnStyle("workshop")}>
                            Pre-Conference Workshop
                            <br />
                            <Badge
                              bg={
                                activePeriod === "workshop" ? "primary" : "info"
                              }
                            >
                              26<sup>th</sup> Sept
                            </Badge>
                            {new Date() > new Date("2025-09-26T23:59:59") && (
                              <Badge bg="secondary" className="ms-1">
                                Closed
                              </Badge>
                            )}
                          </th>
                          <th style={getColumnStyle("early-bird")}>
                            Early Bird Registration
                            <br />
                            <Badge
                              bg={
                                activePeriod === "early-bird"
                                  ? "success"
                                  : "success"
                              }
                              text="dark"
                            >
                              Up to 15<sup>th</sup> August
                            </Badge>
                            {new Date() > new Date("2025-08-15T23:59:59") && (
                              <Badge bg="secondary" className="ms-1">
                                Closed
                              </Badge>
                            )}
                          </th>
                          <th style={getColumnStyle("regular")}>
                            Regular Registration
                            <br />
                            <Badge
                              bg={
                                activePeriod === "regular"
                                  ? "warning"
                                  : "warning"
                              }
                              text="dark"
                            >
                              Up to 10<sup>th</sup> September
                            </Badge>
                            {new Date() > new Date("2025-09-15T23:59:59") && (
                              <Badge bg="secondary" className="ms-1">
                                Closed
                              </Badge>
                            )}
                          </th>
                          <th style={getColumnStyle("onsite")}>
                            On Site Registration
                            <br />
                            <Badge
                              bg={
                                activePeriod === "onsite" ? "danger" : "danger"
                              }
                              text="dark"
                            >
                              From 11<sup>th</sup> Sept Onwards
                            </Badge>
                            {new Date() >= new Date("2025-09-26T00:00:00") && (
                              <Badge bg="success" className="ms-1">
                                Open
                              </Badge>
                            )}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <strong>Oncologist + Physicist Team</strong>
                          </td>
                          <td style={getColumnStyle("workshop")}>
                            ₹ 5000 / $200
                          </td>
                          <td style={getColumnStyle("early-bird")}>
                            ₹ 5000 / $200
                          </td>
                          <td style={getColumnStyle("regular")}>
                            ₹ 5000 / $200
                          </td>
                          <td style={getColumnStyle("onsite")}>
                            ₹ 5000 / $200
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Oncologist or Medical Physicist</strong>
                          </td>
                          <td style={getColumnStyle("workshop")}>
                            ₹ 3000 / $120
                          </td>
                          <td style={getColumnStyle("early-bird")}>
                            ₹ 3000 / $120
                          </td>
                          <td style={getColumnStyle("regular")}>
                            ₹ 3000 / $120
                          </td>
                          <td style={getColumnStyle("onsite")}>
                            ₹ 3000 / $120
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>AMPI & AROI Members</strong>
                          </td>
                          <td style={getColumnStyle("workshop")}>-</td>
                          <td style={getColumnStyle("early-bird")}>₹ 1,500</td>
                          <td style={getColumnStyle("regular")}>₹ 1,750</td>
                          <td style={getColumnStyle("onsite")}>₹ 2,000</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Non-AMPI & -AROI Members</strong>
                          </td>
                          <td style={getColumnStyle("workshop")}>-</td>
                          <td style={getColumnStyle("early-bird")}>₹ 2,000</td>
                          <td style={getColumnStyle("regular")}>₹ 2,500</td>
                          <td style={getColumnStyle("onsite")}>₹ 3,000</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Associate Delegates</strong>
                          </td>
                          <td style={getColumnStyle("workshop")}>-</td>
                          <td style={getColumnStyle("early-bird")}>₹ 1,000</td>
                          <td style={getColumnStyle("regular")}>₹ 1,250</td>
                          <td style={getColumnStyle("onsite")}>₹ 1,500</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Students (Medical Physics, PG, PhD)</strong>
                          </td>
                          <td style={getColumnStyle("workshop")}>-</td>
                          <td style={getColumnStyle("early-bird")}>₹ 1,000</td>
                          <td style={getColumnStyle("regular")}>₹ 1,000</td>
                          <td style={getColumnStyle("onsite")}>₹ 1,500</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>RTT</strong>
                          </td>
                          <td style={getColumnStyle("workshop")}>-</td>
                          <td style={getColumnStyle("early-bird")}>₹ 250</td>
                          <td style={getColumnStyle("regular")}>₹ 500</td>
                          <td style={getColumnStyle("onsite")}>₹ 1000</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Trade Delegates</strong>
                          </td>
                          <td style={getColumnStyle("workshop")}>-</td>
                          <td style={getColumnStyle("early-bird")}>₹ 3,000</td>
                          <td style={getColumnStyle("regular")}>₹ 5,000</td>
                          <td style={getColumnStyle("onsite")}>₹ 7,000</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Overseas Delegates</strong>
                          </td>
                          <td style={getColumnStyle("workshop")}>-</td>
                          <td style={getColumnStyle("early-bird")}>$ 100</td>
                          <td style={getColumnStyle("regular")}>$ 120</td>
                          <td style={getColumnStyle("onsite")}>$ 150</td>
                        </tr>

                        <tr>
                          <td>
                            <strong>AMPI Senior Members(60+)</strong>
                          </td>
                          <td style={getColumnStyle("workshop")}>-</td>

                          <td style={getColumnStyle("early-bird")}>
                            Complimentary (* 1 Rupee Service Charge )
                          </td>
                          <td style={getColumnStyle("regular")}>
                            Complimentary (* 1 Rupee Service Charge )
                          </td>
                          <td style={getColumnStyle("onsite")}>
                            Complimentary (* 1 Rupee Service Charge )
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Apollo Staff</strong>
                          </td>
                          <td style={getColumnStyle("workshop")}>-</td>

                          <td style={getColumnStyle("early-bird")}>
                            Complimentary (* 1 Rupee Service Charge )
                          </td>
                          <td style={getColumnStyle("regular")}>
                            Complimentary (* 1 Rupee Service Charge )
                          </td>
                          <td style={getColumnStyle("onsite")}>
                            Complimentary (* 1 Rupee Service Charge )
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>

                  <h4 className="text-danger mt-4">Notes</h4>
                  <hr />
                  <ul className="list mt-0 mb-4">
                    <li className="mb-2">
                      *AMPI or AROI Member: Please quote your Membership ID no.
                    </li>
                    <li className="mb-2">
                      **Senior AMPI Member: Please attach age proof.
                    </li>
                    <li className="mb-2">
                      ***Student: Please attach your bonafide Certificate.
                    </li>
                    <li className="mb-2">
                      ****Apollo Staff: Please attach proof of employment.
                    </li>
                    <li>
                      Additional charges may apply and will be shown on the
                      payment page.
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            </Tab.Pane>

            <Tab.Pane eventKey="login">
              <Row className="justify-content-center">
                <Col lg={6} className="mb-4">
                  <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
                      <h3 className="mb-0 text-center">
                        Login to Your Account
                      </h3>
                    </Card.Header>
                    <Card.Body className="px-4 pb-4">
                      {showError && (
                        <Alert
                          variant="danger"
                          onClose={() => setShowError(false)}
                          dismissible
                        >
                          {errorMessage}
                        </Alert>
                      )}

                      <Form onSubmit={handleLoginSubmit} className="px-lg-4">
                        <Form.Group controlId="loginEmail" className="mb-4">
                          <Form.Label className="fw-semibold">
                            Email Address <span className="text-danger">*</span>
                          </Form.Label>
                          <div className="input-group">
                            <span className="input-group-text bg-light">
                              <EnvelopeFill />
                            </span>
                            <Form.Control
                              type="email"
                              placeholder="Enter your email address"
                              name="email"
                              value={loginData.email}
                              onChange={handleLoginChange}
                              required
                              className="py-2"
                            />
                          </div>
                        </Form.Group>

                        <Form.Group controlId="loginPassword" className="mb-4">
                          <Form.Label className="fw-semibold">
                            Password <span className="text-danger">*</span>
                          </Form.Label>
                          <div className="input-group">
                            <span className="input-group-text bg-light">
                              <Key />
                            </span>
                            <Form.Control
                              type={showPassword ? "text" : "password"}
                              placeholder="Enter your password"
                              name="password"
                              value={loginData.password}
                              onChange={handleLoginChange}
                              required
                              className="py-2"
                            />
                            <Button
                              variant="outline-secondary"
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="border-start-0"
                            >
                              {showPassword ? <EyeSlash /> : <Eye />}
                            </Button>
                          </div>
                        </Form.Group>
                        <div className="d-grid">
                          <Button
                            type="submit"
                            size="lg"
                            className="py-2 btn-primary"
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  className="me-2"
                                />
                                Logging in...
                              </>
                            ) : (
                              "Login"
                            )}
                          </Button>
                        </div>
                      </Form>

                      <div className="text-center mt-4 pt-3 border-top">
                        <p className="mb-0">
                          Don't have an account?{" "}
                          <span
                            role="button"
                            onClick={() => setActiveTab("registration")}
                            className="link-primary text-decoration-none"
                          >
                            Register Here
                          </span>
                        </p>
                      </div>

                      <div className="text-center mt-4">
                        <p className="text-muted">
                          By logging in, you agree to our Terms of Service and
                          Privacy Policy
                        </p>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col lg={6}>
                  <div className="sticky-top" style={{ top: "2rem" }}>
                    <Card className="border-0 shadow-sm mb-4">
                      <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
                        <h3 className="mb-0">Conference Highlights</h3>
                      </Card.Header>
                      <Card.Body className="px-4 pb-4">
                        <Alert
                          variant="info"
                          className="d-flex align-items-center mb-4"
                        >
                          <Calendar className="me-2" size={20} />
                          <div>
                            <strong>Conference Dates</strong>
                            <p className="mb-0">26th and 27th September 2025</p>
                          </div>
                        </Alert>

                        <ul className="list-unstyled mb-4">
                          <li className="d-flex align-items-center mb-3">
                            <CheckCircle className="text-success me-2" />
                            <span>
                              <strong>Pre-conference Workshop:</strong> Hands-on
                              experience with advanced medical physics
                              techniques
                            </span>
                          </li>
                          <li className="d-flex align-items-center mb-3">
                            <CheckCircle className="text-success me-2" />
                            <span>
                              <strong>Scientific Sessions:</strong> Latest
                              advances in medical physics research and clinical
                              applications
                            </span>
                          </li>
                          <li className="d-flex align-items-center mb-3">
                            <CheckCircle className="text-success me-2" />
                            <span>
                              <strong>Special Panels:</strong> Expert
                              discussions on emerging technologies and
                              challenges
                            </span>
                          </li>
                          <li className="d-flex align-items-center mb-3">
                            <CheckCircle className="text-success me-2" />
                            <span>
                              <strong>Networking:</strong> Connect with leading
                              professionals in the field
                            </span>
                          </li>
                        </ul>

                        <div className="d-grid gap-2">
                          <Button
                            variant="outline-primary"
                            onClick={() => setActiveTab("fees")}
                            className="d-flex align-items-center justify-content-center"
                          >
                            <Info className="me-2" /> View Registration Fees
                          </Button>
                          <Button
                            className="d-flex align-items-center justify-content-center btn-primary"
                            onClick={() => setActiveTab("registration")}
                          >
                            <Person className="me-2" /> Register Now
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                </Col>
              </Row>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Container>
    </div>
  );
};

export default RegistrationPage;
